import { useEventLogging } from '@AppModels/Logging';
import { IRegEdSubmissionMetadata, RegEdComplianceContentType, submitRegEdCompliance } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
	Redirect,
	RouteComponentProps,
	Switch,
	useHistory,
	useLocation,
	useParams,
	useRouteMatch,
} from 'react-router-dom';
import { toast } from 'sonner';
import { ICampaignsCreatedNotificationInfo } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { EmailComposerContext, IEmailComposerContext } from '../../../../models/Email';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { postNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { getDefaultBulkMessagingBodyEditorState } from '../../../../models/UiUtils';
import { invalidateAllInfiniteCampaignReportQueries } from '../../../../queries';
import { useEmailQuery } from '../../../../queries/Email/useEmailQuery';
import { ComposeFollowUpEmailViewModel } from '../../../../viewmodels/AppViewModels';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { ComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt';
import { ComplianceConfirmation } from '../../../components/campaigns/ComplianceConfirmation';
import { SendSchedulerCompleteOverlay } from '../../../components/email/SendSchedulerCompleteOverlay';
import { ScheduledEmailErrorModal } from '../../../components/errorMessages/ScheduledEmailErrorModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { PrivateRoute } from '../../../components/PrivateRoute';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { EditCampaign, IEditCampaignComponent } from '../EditCampaign';
import { EditSendOptions } from '../EditSendOptions';

const MinSendSystemJobRecordCountForOverlay = 5;

const styleSheet = StyleSheet.create({
	childRoute: {
		boxSizing: 'border-box',
		height: '100%',
		overflow: 'auto',
	},
	container: {
		height: '100%',
		width: '100%',
	},
	loaderContainer: {
		alignItems: 'center',
		display: 'flex',
		height: '100%',
		justifyContent: 'center',
	},
});

const _CampaignFollowupPage: React.FC = () => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent, logInput } = useEventLogging('CampaignFollowupPage');
	const history = useHistory();
	const location = useLocation();
	const match = useRouteMatch();
	const { campaignId } = useParams<{ campaignId: string }>();

	const editCampaignRef = React.useRef<IEditCampaignComponent | undefined>(undefined);
	// RegEd submission (format + answers) captured on the compliance prompt; consumed after send()
	// once the campaign (and its server-built approval request) exists.
	const regEdMetadataRef = React.useRef<IRegEdSubmissionMetadata | null>(null);
	// In-progress form draft — survives the back-navigation to the editor (the compliance-options
	// sub-route unmounts the prompt while this page stays mounted).
	const regEdDraftRef = React.useRef<IRegEdSubmissionMetadata | null>(null);
	const [showingCloseConfirmation, setShowingCloseConfirmation] = React.useState(false);
	const [scheduleError, setScheduleError] = React.useState<Api.IOperationResultNoValue | null>(null);
	const [emailComposer, setEmailComposer] = React.useState<ComposeFollowUpEmailViewModel<
		Api.IEmailMessageFollowUpOptions,
		Api.CampaignViewModel
	> | null>(null);

	const {
		data: campaign,
		isLoading,
		error,
	} = useEmailQuery({
		id: campaignId,
		meta: { toastErrorMessage: 'Failed to load campaign' },
	});

	React.useEffect(() => {
		if (!campaign || emailComposer) {
			return;
		}
		const campaignVm = new Api.CampaignViewModel(userSession, campaign);
		const composer = new ComposeFollowUpEmailViewModel<Api.IEmailMessageFollowUpOptions, Api.CampaignViewModel>(
			userSession,
			campaignVm
		);
		// Reproduces the subset of BrowseAndSendCampaigns.configureEmailComposer that applies to this flow:
		// fresh emailMessage, no template, no initial contact selection.
		composer.emailMessage = new Api.EmailMessageViewModel<
			File,
			Api.ISendEmailResponse,
			Api.IEmailMessageFollowUpOptions
		>(userSession);
		composer.allowSenderSelection = false;
		composer.emailMessage.content = getDefaultBulkMessagingBodyEditorState().getRawRichTextContent();
		if (campaign.sendToHousehold) {
			composer.emailMessage.groupByHousehold = true;
		}
		if (campaign.id) {
			composer.emailMessage.options.followUp = {
				_type: 'EmailFollowUpOptions',
				bulkEmailMessageId: campaign.id,
			};
		}
		// Per-recipient modes split into per-owner children at create time, so source.creator is always
		// one human and SelectedUser=creator reproduces the original sender. setSendEmailFromUser
		// populates the resolved user object — the success screen reads sendEmailFromUser.firstName.
		// Gate on canSendOnBehalf so a user reaching this path via crafted locationState (bypassing
		// the UI button) can't have their composer configured to send on someone else's behalf.
		const originalCreator = campaign.creator;
		if (originalCreator?.id && originalCreator.id !== userSession.user.id && userSession.canSendOnBehalf) {
			composer.emailMessage.options.sendEmailFrom = Api.SendEmailFrom.SelectedUser;
			// creator is IUserReference (id/email/firstName/lastName/primaryEmail); copy known fields
			// rather than blanket-casting so future IUser-only consumers don't see missing fields silently.
			// The intermediate Pick<> cast asserts the keys we're assembling are real IUser keys.
			composer.emailMessage.setSendEmailFromUser({
				id: originalCreator.id,
				firstName: originalCreator.firstName,
				lastName: originalCreator.lastName,
				email: originalCreator.email,
				primaryEmail: originalCreator.primaryEmail,
			} as Pick<Api.IUser, 'id' | 'firstName' | 'lastName' | 'email' | 'primaryEmail'> as Api.IUser);
		}
		setEmailComposer(composer);
	}, [campaign, emailComposer, userSession]);

	React.useEffect(() => {
		if (error && !campaign) {
			fullscreenModal?.dismissModal();
		}
	}, [error, fullscreenModal, campaign]);

	const composerContext = React.useMemo<IEmailComposerContext>(
		() => ({ emailComposer: emailComposer ?? undefined }),
		[emailComposer]
	);

	const dismissModal = React.useCallback(() => {
		fullscreenModal?.dismissModal();
	}, [fullscreenModal]);

	const onBackButtonClicked = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			const pathComps = new Set((location.pathname || '').split('/'));
			if (pathComps.has('edit') && pathComps.has('send-complete')) {
				e.preventDefault();
				dismissModal();
			}
			editCampaignRef.current?.finishEditingContent?.();
		},
		[location.pathname, dismissModal]
	);

	const onCloseButtonClicked = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			logInput('Close', 'Click');
			const pathComps = new Set((location.pathname || '').split('/'));
			if (pathComps.has('edit') && !pathComps.has('send-complete')) {
				e.preventDefault();
				setShowingCloseConfirmation(true);
			}
		},
		[location.pathname, logInput]
	);

	const onCloseConfirmationDialogRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			setShowingCloseConfirmation(false);
			if (result?.isDestructive && !canceled) {
				dismissModal();
			}
		},
		[dismissModal]
	);

	const onCloseScheduleErrorModal = React.useCallback(() => {
		setScheduleError(null);
	}, []);

	const showScheduleComplete = React.useCallback(
		(schedule: Api.IScheduledSend) => {
			if (!emailComposer) {
				return;
			}
			emailComposer.sendEstimate = {
				...(emailComposer.sendEstimate || {}),
				estimate: schedule,
			};
			if (emailComposer?.emailMessage?.groupId) {
				invalidateAllInfiniteCampaignReportQueries();
			}
			history.push(
				`${match.url}/${
					emailComposer.emailMessage.options?.sendWithCompliance ? 'edit/compliance-complete' : 'edit/send-complete'
				}`
			);
		},
		[emailComposer, history, match.url]
	);

	const send = React.useCallback(async () => {
		if (!emailComposer) {
			return;
		}
		try {
			const op = await emailComposer.emailMessage.send();
			const sentCampaign = new Api.CampaignViewModel(userSession, { id: op.value.id });
			try {
				logEvent('LoadCampaign', { id: op.value.id });
				await sentCampaign.load();
			} catch (err) {
				logApiError('LoadCampaign-Error', err as Api.IOperationResultNoValue);
			}

			const regEdMetadata = regEdMetadataRef.current;
			// Isolated: the campaign is already sent, so a submission failure must not surface it as a
			// send error (which would drop the success path and invite a retry → duplicate approval).
			await submitRegEdCompliance({
				userSession,
				parentType: RegEdComplianceContentType.Email,
				parentId: op.value.id,
				approvalRequestId: sentCampaign.toJs().approvalRequests?.[0]?.id,
				metadata: regEdMetadata,
				logApiError,
				action: 'sent',
			});

			const info: ICampaignsCreatedNotificationInfo = {
				accountId: userSession.account.id,
				campaigns: [sentCampaign.toJs()],
			};
			postNotification({
				info,
				topic: Topics.CREATE_CAMPAIGNS_ITEM,
			});

			if (op.value.schedule.criteria === Api.ScheduleCriteria.Immediately) {
				if (sentCampaign.totalCount < MinSendSystemJobRecordCountForOverlay) {
					postNotification({
						info: emailComposer.emailMessage,
						topic: Topics.SEND_EMAIL,
					});
					toast.success('Message sent successfully');
					dismissModal();
				} else {
					toast.success('Success. Your email is ready to send.');
					history.push(`/reporting/group-email/${op.value.id}`);
				}
			} else {
				showScheduleComplete(op.value.schedule);
			}
		} catch (sendError: any) {
			logApiError('SendBulk-Error', sendError);
			const sendNow = emailComposer.emailMessage.options?.scheduledSend?.criteria === Api.ScheduleCriteria.Immediately;
			if (!sendNow && sendError?.systemCode === -1) {
				setScheduleError(sendError);
				return;
			}
			errorMessages.pushApiError(sendError);
		}
	}, [emailComposer, userSession, logEvent, logApiError, errorMessages, history, dismissModal, showScheduleComplete]);

	const onSendWithOutCompliance = React.useCallback(() => {
		if (!emailComposer) {
			return;
		}
		emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
		emailComposer.emailMessage.options.sendWithCompliance = false;
		history.push(`${match.url}/edit/send-options`);
	}, [emailComposer, history, match.url]);

	const onSendWithCompliance = React.useCallback(
		(sendWithComplianceEmail: string) => {
			if (!emailComposer) {
				return;
			}
			emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
			emailComposer.emailMessage.options.sendWithCompliance = true;
			emailComposer.emailMessage.options.sendWithComplianceEmail = sendWithComplianceEmail;
			history.push(`${match.url}/edit/send-options`);
		},
		[emailComposer, history, match.url]
	);

	const onSendWithRegEdCompliance = React.useCallback(
		(metadata: IRegEdSubmissionMetadata) => {
			if (!emailComposer) {
				return;
			}
			regEdMetadataRef.current = metadata;
			emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
			emailComposer.emailMessage.options.sendWithCompliance = true;
			history.push(`${match.url}/edit/send-options`);
		},
		[emailComposer, history, match.url]
	);

	const renderFullscreenHeader = () => {
		if (emailComposer?.isUserEditingMessage) {
			return null;
		}
		let title = '';
		if (location.pathname === `${match.url}/edit/send-options`) {
			title = 'Send Options';
		} else if (location.pathname === `${match.url}/edit/compliance-options`) {
			title = 'Compliance Approval';
		} else if (location.pathname === `${match.url}/edit/compliance-complete`) {
			title = 'Confirmation';
		} else if (new RegExp(`^${match.url}/edit`, 'i').test(location.pathname)) {
			title = 'View Campaign';
		}
		if (!title) {
			return null;
		}
		return (
			<MultiContainerHeader
				fullscreenHeader={title}
				onFullscreenRequestBack={onBackButtonClicked}
				onFullscreenRequestClose={onCloseButtonClicked}
			/>
		);
	};

	if (isLoading || !emailComposer) {
		return (
			<div className={`${css(styleSheet.container)} campaign-followup-page`}>
				<MultiContainerHeader
					fullscreenHeader='View Campaign'
					onFullscreenRequestBack={onBackButtonClicked}
					onFullscreenRequestClose={onCloseButtonClicked}
				/>
				<div className={css(styleSheet.loaderContainer)}>
					<LoadingSpinner type='large' />
				</div>
			</div>
		);
	}

	const onRenderEditCampaign = (props: RouteComponentProps<any>) => (
		<EditCampaign
			{...props}
			emailComposer={emailComposer}
			onInnerRef={(ref?: IEditCampaignComponent) => {
				editCampaignRef.current = ref;
			}}
			styles={[styleSheet.childRoute]}
		/>
	);

	const onRenderEditSendOptions = (props: RouteComponentProps<any>) => (
		<EditSendOptions
			{...props}
			disableSendNow={!emailComposer.emailMessage.isSendFromOption(Api.SendEmailFrom.CurrentUser)}
			emailComposer={emailComposer}
			onScheduleCtaClicked={send}
			styles={[styleSheet.childRoute]}
		/>
	);

	const onRenderComplianceOptions = (props: RouteComponentProps<any>) => (
		<ComplianceApprovalPrompt
			{...props}
			campaignType={CampaignType.Email}
			regEdInitialFormat={regEdDraftRef.current?.format}
			regEdInitialAnswers={regEdDraftRef.current?.answers}
			onRegEdFormStateChange={metadata => {
				regEdDraftRef.current = metadata;
			}}
			onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
				e.preventDefault();
				onSendWithCompliance(sendWithComplianceEmail);
			}}
			onScheduleRegEdCompliance={(e, metadata) => {
				e.preventDefault();
				onSendWithRegEdCompliance(metadata);
			}}
			onScheduleWithoutCompliance={onSendWithOutCompliance}
		/>
	);

	const onRenderSendComplete = (props: RouteComponentProps<any>) => (
		<SendSchedulerCompleteOverlay
			{...props}
			emailComposer={emailComposer}
			emailCount={emailComposer.estimatedRecipientsTotal}
			estimate={emailComposer.sendEstimate}
			onCloseButtonClicked={dismissModal}
			styles={[styleSheet.childRoute]}
		/>
	);

	return (
		<div className={`${css(styleSheet.container)} campaign-followup-page`}>
			<EmailComposerContext.Provider value={composerContext}>
				{renderFullscreenHeader()}
				<Switch>
					<Redirect exact={true} from={match.url} to={`${match.url}/edit/recipients/follow-up`} />
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/send-options`}
						render={onRenderEditSendOptions}
						userSession={userSession}
					/>
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/compliance-options`}
						render={onRenderComplianceOptions}
						userSession={userSession}
					/>
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/send-complete`}
						render={onRenderSendComplete}
						userSession={userSession}
					/>
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/compliance-complete`}
						userSession={userSession}
					>
						<ComplianceConfirmation onDismiss={dismissModal} styles={[styleSheet.childRoute]} />
					</PrivateRoute>
					<PrivateRoute
						location={location}
						path={`${match.url}/edit`}
						render={onRenderEditCampaign}
						userSession={userSession}
					/>
				</Switch>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: showingCloseConfirmation,
						onRequestClose: onCloseConfirmationDialogRequestClose,
					}}
					options={[
						{ isDestructive: true, representedObject: true, title: 'Discard' },
						{ isCancel: true, representedObject: false, title: 'Cancel' },
					]}
					title='Are you sure you want to discard this email?'
				/>
				<ScheduledEmailErrorModal
					onNavigateToReportingGroupEmail={dismissModal}
					onRequestClose={onCloseScheduleErrorModal}
					scheduleError={scheduleError}
				/>
			</EmailComposerContext.Provider>
		</div>
	);
};

export const CampaignFollowupPage = observer(_CampaignFollowupPage);
